import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useWindowWidth } from '@react-hook/window-size';
import { CategoriesPickerV2 } from 'components/components/CategoriesPickerV2';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { getLabel, getSortedItems } from './CategoriesContainer.helpers';
import * as Styled from './CategoriesContainer.styles';
import { searchActionsEnum } from '../../config/constants';
import { useFiltersContext } from '../../contexts';
import { useUpdateEffect } from '../../hooks/customHooks';
import { definedTranslation } from '../../i18n';
import {
  appFiltersKeysSelector,
  appFiltersSelector,
  isFilterShownSelector,
  taxonomyChildrenFilterSelector,
  taxonomyFilterValueSelector,
  taxonomyParentsFilterSelector,
  taxonomySelector,
} from '../../redux/selectors/filtersSelector';
import {
  searchOptionsSelector,
  searchResultsSelector,
} from '../../redux/selectors/searchSelector';
import {
  addFilter,
  removeFilter,
  setAppFilters,
  setIsRainbowClicked,
  toggleFiltersSidebar,
} from '../../redux/sliceCreators/filtersSlice';
import { FilterData } from '../../types';
import { getCharsNumber } from '../../utils/helpers';

const taxonomyFacetField = 'taxonomy';

interface CategoriesContainerProps {
  addToCartButtonWidth?: number;
  fullWidth?: boolean;
}

const CategoriesContainer: FC<CategoriesContainerProps> = ({
  addToCartButtonWidth,
  fullWidth = false,
}) => {
  const { getNewFilters } = useFiltersContext();
  const results = useSelector(searchResultsSelector);
  const appliedFilters = useSelector(appFiltersSelector);
  const currentFilterKeys = useSelector(appFiltersKeysSelector);
  const searchOptions = useSelector(searchOptionsSelector);
  const taxonomyFacet = useSelector(taxonomySelector);
  const taxonomyParents = useSelector(taxonomyParentsFilterSelector);
  const taxonomyChildren = useSelector(taxonomyChildrenFilterSelector);
  const isFilterShown = useSelector(isFilterShownSelector);
  const taxonomySearchValue = useSelector(taxonomyFilterValueSelector);
  const [tabFilters, setTabFilters] = useState<FilterData[]>([]);
  const windowWidth = useWindowWidth();
  const dispatch = useDispatch();
  const intl = useIntl();
  const allButtonText = intl.formatMessage(
    definedTranslation.filters.allCategories,
  );

  const isNewRequestAction = useMemo(() => {
    const action = searchOptions?.action;
    const actionsToChangeTabs = [
      searchActionsEnum.init,
      searchActionsEnum.searchBar,
      searchActionsEnum.initPrevResults,
      searchActionsEnum.reset,
      searchActionsEnum.resetFilters,
    ];
    const isCleanRequest = currentFilterKeys?.length === 0;
    return (
      actionsToChangeTabs.includes(action || '') ||
      isCleanRequest ||
      action == null
    );
  }, [searchOptions, currentFilterKeys]);

  const {
    isSubcategorySelected,
    parentLabel,
    isMultipleParents,
    isAnyParentInCurrentFilters,
  } = useMemo(() => {
    const firstSelectedChild = currentFilterKeys?.find((item) => {
      return (
        item.includes(taxonomyFacetField) && getCharsNumber(item, '>') === 2
      );
    });
    const taxonomyFilters = currentFilterKeys?.filter((item) => {
      return item.includes(taxonomyFacetField);
    });
    const isAnyParentInCurrentFilters = taxonomyFilters.some(
      (item) => getCharsNumber(item, '>') === 1,
    );
    const parentLabels = taxonomyFilters?.map((item) => item?.split('>')?.[1]);
    const uniqParents = uniq(parentLabels);
    return {
      parentLabel: firstSelectedChild?.split('>')?.[1],
      isSubcategorySelected: firstSelectedChild != null,
      isMultipleParents: uniqParents?.length > 1,
      isAnyParentInCurrentFilters,
    };
  }, [currentFilterKeys]);

  const { isParentSelected, selectedParentLabel, sortedParents } =
    useMemo(() => {
      const selectedParent = taxonomyParents?.find(
        (item: FilterData) => item.active,
      );
      return {
        isParentSelected: Boolean(selectedParent) || isSubcategorySelected,
        selectedParentLabel: getLabel(selectedParent?.label, 1),
        selectedParentCount: selectedParent?.count,
        sortedParents: getSortedItems(taxonomyParents),
      };
    }, [taxonomyParents, isSubcategorySelected]);

  const appliedFiltersNumber = useMemo(() => {
    return (currentFilterKeys || []).length;
  }, [currentFilterKeys]);

  const resetChildrenTaxonomyState = useCallback(() => {
    const sortedCategories = getSortedItems(taxonomyChildren);
    setTabFilters(sortedCategories);
  }, [taxonomyChildren]);

  useEffect(() => {
    if (isNewRequestAction) {
      resetChildrenTaxonomyState();
    }
  }, [taxonomyChildren, isNewRequestAction, resetChildrenTaxonomyState]);

  useUpdateEffect(() => {
    resetChildrenTaxonomyState();
  }, [isParentSelected]);

  useUpdateEffect(() => {
    const currentFilters = currentFilterKeys;
    if (isMultipleParents) {
      setTabFilters([]);
    }
    setTabFilters((prev) => {
      if (prev?.length === 0) return prev;

      return prev.map((item) => {
        return !item.active
          ? item
          : { ...item, active: currentFilters?.includes(item.id) };
      });
    });
  }, [currentFilterKeys]);

  const tabsData = useMemo(() => {
    const finalChildrenData = getSortedItems(taxonomyChildren);
    const finalData = !isParentSelected ? sortedParents : finalChildrenData;
    const labelArrowsNumber = !isParentSelected ? 1 : 2;

    return finalData?.map((sub) => ({
      id: sub.id,
      title: getLabel(sub.label, labelArrowsNumber),
      selected: sub.active,
      counter: sub.count,
      filter: sub,
    }));
  }, [taxonomyChildren, isParentSelected, sortedParents]);

  // Handle Edge-case, when from response.override comes multiple parents, which can't be
  // done in FE by user. Moved from memo-tabsData, to not mess with localState for tabs
  const tabsWithMultiParents = useMemo(() => {
    const onlyActiveChildren = taxonomyFacet?.filter(
      (item: FilterData) =>
        item.active && getCharsNumber(item?.label, '>') === 2,
    );
    const labelArrowsNumber = !isParentSelected ? 1 : 2;
    return onlyActiveChildren?.map((sub: FilterData) => ({
      id: sub.id,
      title: getLabel(sub.label, labelArrowsNumber),
      selected: sub.active,
      counter: sub.count,
      filter: sub,
    }));
  }, [taxonomyFacet, isParentSelected]);

  const handleFilterToggle = () => {
    dispatch(toggleFiltersSidebar(null));
  };

  const handleUpdateItems = (
    filter: FilterData | null,
    currentTabs: FilterData[],
  ) => {
    const currentLocalFilter = currentTabs.find(
      (item) => item.id === filter?.id,
    );
    if (currentLocalFilter != null) {
      setTabFilters((prev) => {
        const updatedList = prev.map((item) => {
          return item.id === filter?.id
            ? { ...item, active: !filter.active }
            : item;
        });
        return getSortedItems(updatedList);
      });
    }
  };

  const handleCategoryClick = (filter?: FilterData) => {
    if (filter == null) return;
    const filterLabel = getLabel(filter?.label);
    const filterData = {
      ...filter,
      parent: filterLabel,
    };
    if (filter?.active) {
      dispatch(removeFilter(filterData));
    } else {
      dispatch(addFilter([filterData]));
    }
    handleUpdateItems(filter, tabFilters);
  };

  const handleFiltersClick = (filter: FilterData) => {
    if (filter?.active) {
      dispatch(removeFilter({ id: filter.id }));
    } else {
      const [key, value] = filter?.id?.split('=');
      const newFilter = getNewFilters(key, value, appliedFilters);
      dispatch(addFilter(newFilter));
    }
  };

  const resetTaxonomyFilters = () => {
    const filterKeys = currentFilterKeys.filter((key) => {
      return key.includes(taxonomyFacetField);
    });
    const filteredFilters = omit(appliedFilters, filterKeys);
    dispatch(setAppFilters(filteredFilters));
  };

  const handleSeeAllClick = () => {
    if (isSubcategorySelected) {
      // Clear all filters beside Taxonomy parent
      const filterKeys = currentFilterKeys.filter((key) => {
        const arrowsNumber = getCharsNumber(key, '>');
        return !(key.includes(taxonomyFacetField) && arrowsNumber === 1);
      });
      const filteredFilters: Record<
        string,
        {
          id: string;
          field: string;
          label: string;
          value?: string;
        }
      > = omit(appliedFilters, filterKeys);
      if (!isAnyParentInCurrentFilters) {
        // Edge case, when coming from override different children from different parent
        // and removed one by one, till All [Parent] button will appear.
        const firstTaxonomyChild = currentFilterKeys?.find((key) => {
          const arrowsNumber = getCharsNumber(key, '>');
          return key.includes(taxonomyFacetField) && arrowsNumber === 2;
        });
        if (firstTaxonomyChild != null) {
          const parentLabelRaw = firstTaxonomyChild
            ?.split('=')?.[1]
            ?.split('>');
          parentLabelRaw.pop();
          const parentLabel = parentLabelRaw.join('>');
          if (parentLabel != null) {
            const newId = `filter.${taxonomyFacetField}=${parentLabel}`;
            filteredFilters[newId] = {
              id: newId,
              field: taxonomyFacetField,
              label: parentLabel,
              value: parentLabel,
            };
          }
        }
      }
      dispatch(setAppFilters(filteredFilters));
    } else {
      resetTaxonomyFilters();
    }
    dispatch(setIsRainbowClicked(false));
  };

  const isShowAllActive = !isParentSelected
    ? appliedFiltersNumber === 0
    : !isSubcategorySelected;
  const showAllLabel = isParentSelected
    ? `${allButtonText} ${selectedParentLabel || parentLabel}`
    : allButtonText;
  const shouldShowAllButton = !isMultipleParents;

  return (
    <Styled.CatagoriesWrapper
      addToCartButtonWidth={addToCartButtonWidth}
      fullWidth={fullWidth}
      id="topbar_filters_products"
    >
      <CategoriesPickerV2
        shouldShowSecondLine={false}
        shouldShowAllButton={shouldShowAllButton}
        showAllLabel={showAllLabel}
        tabs={isMultipleParents ? tabsWithMultiParents : tabsData}
        shouldShowBackButton={isParentSelected}
        rightElementWidth={420}
        shouldResetScroll={
          isNewRequestAction || (isParentSelected && !isSubcategorySelected)
        }
        shouldShowFiltersToggle={windowWidth > 1024}
        showAllNumber={undefined}
        appliedFiltersNumber={isFilterShown ? 0 : appliedFiltersNumber}
        isShowAllActive={isShowAllActive}
        isFilterActive={isFilterShown}
        onFilterToggle={handleFilterToggle}
        onTabClick={handleCategoryClick}
        onSeeAllClick={handleSeeAllClick}
        onSubTabsClick={handleFiltersClick}
        onBackButtonClick={resetTaxonomyFilters}
        filterValue={taxonomySearchValue}
        backButtonText={allButtonText}
      />
    </Styled.CatagoriesWrapper>
  );
};

export default CategoriesContainer;
